"use client";

import clsx from "clsx";
import type { ComplianceSnapshot as Snapshot, ComplianceAuthorityRow } from "@/lib/sse/useSSE";
import { CdmState } from "@/components/cdm/CdmState";
import { isPublic } from "@/lib/profile";
import { PrivateOnlyMark, PrivateBadge } from "@/components/scene/PrivateOnlyMark";
import { RegulatorEvidence } from "@/components/scenes/shared/RegulatorEvidence";
import { useTranslations } from "@/lib/compat/i18n";

// On the public profile, drop any authority row whose name names a
// compliance surface (sanctions, Travel Rule, compliance hard stops).
// Tolerate both spaced ("Travel Rule") and concatenated ("TravelRule")
// forms — the SSE emits the no-space variant; the skeleton list uses
// the spaced one.
function isComplianceAuthority(name: string): boolean {
  const n = name.toLowerCase().replace(/[\s_-]+/g, "");
  return n.includes("sanction") || n.includes("travelrule") || n.includes("compliancehardstop");
}

/**
 * Cross-Border Compliance Snapshot (screen 2.6 / 4.13 / 5.12). The audience
 * sees the Compliance module activate with per-authority readiness rows.
 *
 *   If no snapshot has fired yet, we render a SKELETON of the same layout
 *   (regulator rows showing "pending") rather than a blank "not produced"
 *   message — eliminates the dead-screen moment the cofounders flagged.
 *
 *   Output: a Corridor Compliance Snapshot artefact attached to the contract;
 *   consumed by the LC gate cascade.
 */

// Default skeleton authority list when no scenario-specific list is passed.
const DEFAULT_EXPECTED_AUTHORITIES = [
  "Receita Federal",
  "Siscomex",
  "MAPA",
  "UAE FTA",
  "Dubai Trade",
  "MOCCAE",
  "BR + UAE sanctions screening",
  "Compliance hard stops",
];

export function ComplianceSnapshot({
  snapshot,
  className,
  expectedAuthorities = DEFAULT_EXPECTED_AUTHORITIES,
  expectedCorridorLabel = "Cross-border corridor · regulator snapshot",
}: {
  snapshot: Snapshot | null;
  className?: string;
  expectedAuthorities?: string[];
  expectedCorridorLabel?: string;
}) {
  const pub = isPublic();
  const filteredExpected = pub
    ? expectedAuthorities.filter((n) => !isComplianceAuthority(n))
    : expectedAuthorities;
  const t = useTranslations("complianceSnapshot");
  if (!snapshot) {
    return (
      <div className={clsx("grid h-full min-h-0 grid-cols-3 gap-4", className)}>
        <div className="glass col-span-2 flex min-h-0 flex-col overflow-y-auto p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("preparingKicker")}</p>
              <h3 className="scene-h2 mt-1 whitespace-pre-line text-orbitra-paper">{expectedCorridorLabel}</h3>
            </div>
            <span className="rounded-full border border-orbitra-muted/30 bg-orbitra-muted/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-orbitra-muted">
              {t("snapshotInFlight")}
            </span>
          </div>

          <ul className="mt-4 flex flex-col gap-1.5">
            {filteredExpected.map((name) => (
              <li
                key={name}
                className="flex items-center gap-3 rounded-md border border-orbitra-muted/30 bg-orbitra-muted/8 px-3 py-2 text-[11px] text-orbitra-muted"
              >
                <span className="flex-1">{name}</span>
                <span className="font-mono text-[9px] uppercase tracking-wider opacity-70">{t("pendingTag")}</span>
                <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-orbitra-muted/50" />
              </li>
            ))}
          </ul>

          <div className="mt-auto rounded-md border border-orbitra-muted/30 bg-orbitra-ink/40 px-3 py-2 text-[11px] text-orbitra-muted">
            {t("aggregatingNote")}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="glass p-4">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("vatTeaser")}</p>
            <p className="mt-2 font-display text-3xl font-semibold text-orbitra-muted/60">— %</p>
            <p className="text-[11px] text-orbitra-muted">{t("prepPending")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx("grid h-full min-h-0 grid-cols-3 gap-4", className)}>
      <div className="glass col-span-2 flex min-h-0 flex-col overflow-y-auto p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("corridorPrefix")} <span dir="ltr">{snapshot.snapshotId}</span></p>
            <h3 className="scene-h2 mt-1 text-orbitra-paper">Corridor {snapshot.corridorId} · Brazil (origin) → UAE (destination)</h3>
          </div>
          <span className="rounded-full border border-state-traced/40 bg-state-traced/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-traced">
            {t("moduleActivatedTag")}
          </span>
        </div>

        <ul className="mt-4 flex flex-col gap-1.5">
          {(pub ? snapshot.authorities.filter((a) => !isComplianceAuthority(a.name)) : snapshot.authorities).map((a) => (
            <AuthorityRow key={a.name} row={a} privateOnly={isComplianceAuthority(a.name)} />
          ))}
        </ul>

        <SanctionsCadencePanel />

        {!pub && <TravelRuleDataPanel />}

        {!pub && (
          <div className="mt-auto flex items-start gap-2 rounded-md border border-state-financed/30 bg-state-financed/10 px-3 py-2 text-[11px] text-state-financed">
            <PrivateBadge className="mt-0.5 shrink-0" />
            <span>
              {t("travelRuleWarning")}
            </span>
          </div>
        )}

        <RegulatorEvidence
          variant="compliance-snapshot"
          corridorId={snapshot.corridorId}
          snapshotId={snapshot.snapshotId}
          className="mt-3"
        />
      </div>

      <div className="flex flex-col gap-3">
        <div className="glass p-4">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("vatTeaser")}</p>
          <p className="mt-2 font-display text-3xl font-semibold text-state-traced">{snapshot.vatPrefillSavingPct ?? 78}%</p>
          <p className="text-[11px] text-orbitra-paper">{t("prepDone")}</p>
          <p className="mt-2 text-[10px] text-orbitra-muted">{t("prepFooter")}</p>
        </div>
        {!pub && (
          <PrivateOnlyMark>
            <div className="glass p-4 text-[11px]">
              <p className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("vegaAgentLabel")}</p>
              <p className="mt-2 text-orbitra-paper italic">"Before we go to settlement, here's the corridor view. I've been watching since you onboarded."</p>
            </div>
          </PrivateOnlyMark>
        )}
      </div>
    </div>
  );
}

/**
 * TravelRuleDataPanel — surfaces the originator + beneficiary fields that
 * the FATF Travel Rule actually requires institutions to share across a
 * cross-border value transfer. Investors and regulators usually ask
 * "what data is being passed?" — and the demo previously fired a single
 * `policy.Decided` gate without showing the payload. This panel makes
 * the data flow explicit.
 *
 * Private-only — visible on `pdemo` and full-profile only.
 */
function TravelRuleDataPanel() {
  const t = useTranslations("complianceSnapshot");
  return (
    <div className="mt-3 rounded-md border border-state-financed/30 bg-state-financed/8 px-3 py-2 text-[11px]">
      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
        Travel Rule · originator → beneficiary payload
      </p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <div>
          <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-orbitra-muted">{t("originatorLabel")}</p>
          <ul className="mt-1 space-y-0.5 text-orbitra-paper">
            <li>{t("originatorValue")}</li>
            <li className="font-mono text-[10px] text-state-financed">LEI · 5493-AVES-AURORA-BR</li>
            <li className="text-orbitra-muted">{t("jurisdictionBr")}</li>
            <li className="text-orbitra-muted">{t("originatorAccount")}</li>
          </ul>
        </div>
        <div>
          <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-orbitra-muted">{t("beneficiaryLabel")}</p>
          <ul className="mt-1 space-y-0.5 text-orbitra-paper">
            <li>{t("beneficiaryValue")}</li>
            <li className="font-mono text-[10px] text-state-financed">LEI · 5493-ALMADAR-AE</li>
            <li className="text-orbitra-muted">{t("jurisdictionAe")}</li>
            <li className="text-orbitra-muted">{t("beneficiaryAccount")}</li>
          </ul>
        </div>
      </div>
      <p className="mt-2 font-mono text-[9px] text-orbitra-muted">
        Shared institution-to-institution under FATF Recommendation 16 · IVMS101 envelope.
      </p>
    </div>
  );
}

/**
 * SanctionsCadencePanel — surfaces the sanctions-list freshness across the
 * five lists that "BR + UAE sanctions screening" actually runs against.
 * Real-world sanctions screening is continuous, not a single PASS gate;
 * showing each list's version number + refresh time grounds the cadence
 * for audiences (compliance officers especially) who notice the
 * abstraction.
 *
 * Values are illustrative — the demo doesn't ingest a live feed.
 */
function SanctionsCadencePanel() {
  const lists = [
    { name: "OFAC SDN",     version: "v2026.04", refreshed: "08:00 GST" },
    { name: "EU CFSP",      version: "v2026.04", refreshed: "08:00 GST" },
    { name: "UN consolidated", version: "v2026.03", refreshed: "07:45 GST" },
    { name: "UK HMT",       version: "v2026.04", refreshed: "08:00 GST" },
    { name: "Local · BR + AE", version: "v2026.04", refreshed: "08:15 GST" },
  ];
  return (
    <div className="mt-3 rounded-md border border-state-traced/30 bg-state-traced/8 px-3 py-2 text-[10px] text-state-traced">
      <p className="font-mono text-[9px] uppercase tracking-[0.18em]">
        Sanctions monitor · continuous · last cycle 14:18 GST
      </p>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
        {lists.map((l) => (
          <span key={l.name} className="inline-flex items-baseline gap-1.5">
            <span className="text-orbitra-paper">{l.name}</span>
            <span className="font-mono text-state-traced">{l.version}</span>
            <span className="font-mono text-orbitra-muted">· {l.refreshed}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * One-line role-of-this-regulator descriptions. Surfaced as a hover
 * tooltip on each authority row so audiences who don't know the acronyms
 * still understand why each one is in the chain. Covers the regulators
 * referenced by T1/T2/T3 scenarios + the platform-side checks.
 */
const REGULATOR_ROLES: Record<string, string> = {
  "UAE FTA":                          "UAE Federal Tax Authority — VAT clearance + e-invoice authority for imports into the UAE.",
  "E-invoicing and VAT (UAE)":        "UAE Federal Tax Authority — VAT clearance + e-invoice authority for imports into the UAE.",
  "Dubai Trade":                      "Dubai's single-window customs portal — bundles port, customs, and shipping into one declaration.",
  "MOCCAE":                           "UAE Ministry of Climate Change & Environment — issues phytosanitary clearance + food import licences for AE imports.",
  "MOCCAE Porto":                     "MOCCAE Porto — UAE Ministry of Climate Change & Environment port-side import-licence + phytosanitary clearance gateway.",
  "ATLP AD Ports":                    "ATLP — Abu Dhabi Ports' Advanced Trade & Logistics Platform; the AD Ports single-window customs gateway used in place of the Dubai Trade portal when goods clear via Abu Dhabi.",
  "MOIAT":                            "UAE Ministry of Industry & Advanced Technology — industrial goods + product registration.",
  "ABCC":                             "Arab-Brazilian Chamber of Commerce — accredited issuer of BR Certificates of Origin for the Arab corridor.",
  "FUC":                              "Federation of UAE Chambers — UAE-side Chamber of Commerce, attests UAE-origin Certificates of Origin.",
  "CDIAL Halal":                      "Brazilian halal certifier accredited by GCC authorities — Slaughter-Cert. issuer for poultry exports to AE.",
  "MAPA":                             "Brazilian Ministry of Agriculture, Livestock & Food Supply — issues Veterinary Health Certificates.",
  "MAPA — Import Mode":               "Brazilian Ministry of Agriculture, Livestock & Food Supply (import mode) — vets agricultural inbound shipments.",
  "BR + UAE sanctions screening":     "Continuous sanctions screening across OFAC, EU CFSP, UN consolidated, UK HMT, and local lists (BR + UAE).",
  "OFAC + UAE Sanctions (Internal Use Only)": "Internal Orbitra screening rail — continuous sanctions screening across OFAC, EU CFSP, UN consolidated, UK HMT, and local lists (BR + UAE).",
  "Travel Rule":                      "FATF Travel Rule — originator + beneficiary identity data shared between participating institutions for cross-border value transfers.",
  "TravelRule (Internal Use Only)":   "Internal Orbitra check — FATF Travel Rule originator + beneficiary identity data shared between participating institutions for cross-border value transfers.",
  "Compliance Hard Stops (Internal Use Only)": "Internal Orbitra policy gates — hard-stop checks that block settlement if any compliance precondition fails.",
  "Banco Central do Brasil":          "Brazil's central bank — FX + cross-border payment authorisation.",
  "Receita Federal":                  "Brazilian federal revenue / customs — import + export declarations.",
  "Receita Federal (BRL)":            "Brazilian federal revenue / customs (BRL settlement) — import + export declarations.",
  "SGS":                              "Independent surveyor — pre-shipment cargo inspection + bulk quality attestations.",
};

function AuthorityRow({ row, privateOnly = false }: { row: ComplianceAuthorityRow; privateOnly?: boolean }) {
  const tone =
    row.outcome === "PASS"
      ? "border-policy-pass/40 bg-policy-pass/8 text-policy-pass"
      : row.outcome === "INDETERMINATE"
        ? "border-policy-indeterminate/40 bg-policy-indeterminate/10 text-policy-indeterminate"
        : "border-policy-fail/40 bg-policy-fail/10 text-policy-fail";
  const role = REGULATOR_ROLES[row.name];
  const t = useTranslations("complianceSnapshot");
  return (
    <li
      title={role ?? undefined}
      className={clsx(
        "flex items-center gap-3 rounded-md border px-3 py-2 text-[11px]",
        tone,
        privateOnly && !isPublic() && "border-s-2 border-s-state-financed",
        role && "cursor-help",
      )}
    >
      {privateOnly && !isPublic() && (
        <span aria-hidden="true" title={t("hiddenOnPublicTitle")} className="text-[10px] leading-none">🛑</span>
      )}
      <span className="flex-1 text-orbitra-paper">
        {row.name}
        {role && <span aria-hidden="true" className="ms-1 text-[9px] text-orbitra-muted/70">ⓘ</span>}
      </span>
      {row.snapshotAt && <span className="font-mono text-[9px] text-orbitra-muted">{row.snapshotAt}</span>}
      {row.note && <span className="font-mono text-[9px] italic">{row.note}</span>}
      <CdmState token={row.outcome} className="px-1.5 py-0.5" />
    </li>
  );
}
